import React, { useMemo } from 'react';
import { useLoaderData } from '@remix-run/react';
import { twMerge } from 'tailwind-merge';
import DebugComponent from '~/components/debug.tsx';
import UiComponent from '~/components/ui-component.tsx';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import type { Feed, UIComponent } from '~/services/layout/index.ts';
import type { ServerLoadedFeeds } from '~/services/layout/server-loaded-feeds.ts';
import {
  useIsLoggedIn,
  type UseIsLoggedInReturnType,
} from '~/hooks/use-is-logged-in';
import { useScrollRestorationOnLazyLoading } from '~/hooks/use-scroll-restoration';
import type { FormUiComponent } from '../types.ts';

type FormScreenContentProps = {
  uiComponents: FormUiComponent[];
  serverLoadedFeeds: ServerLoadedFeeds;
};

const EMPTY_STORAGE_FEEDS_DATA = {
  'continue-watching': {
    feed: undefined,
    isUpdating: false,
    isLoading: false,
  },
  favorites: {
    feed: undefined,
    isUpdating: false,
    isLoading: false,
  },
} as const;

/**
 * Renders dynamic form screen components through the shared UI component pipeline.
 */
export function FormScreenContent({
  uiComponents,
  serverLoadedFeeds,
}: FormScreenContentProps): JSX.Element {
  const { debug, screen, isThemeV2 } = useLoaderData<typeof rootLoader>();

  useScrollRestorationOnLazyLoading();

  const isLoggedInData: UseIsLoggedInReturnType = useIsLoggedIn();

  const uiComponentProps = useMemo(
    () => ({
      isLoggedInData,
      storageFeedsData: EMPTY_STORAGE_FEEDS_DATA,
    }),
    [isLoggedInData]
  );

  const feedsByComponentId = useMemo(() => {
    const map = new Map<string, ServerLoadedFeeds[number]>();

    for (const loadedFeed of serverLoadedFeeds) {
      if (loadedFeed.componentId != null) {
        map.set(loadedFeed.componentId, loadedFeed);
      }
    }

    return map;
  }, [serverLoadedFeeds]);

  return (
    <div
      className={twMerge(
        `screen-${screen.id}`,
        'z-0 w-full',
        isThemeV2
          ? 'screen-classes'
          : 'mb-screen-b mt-screen-t min-h-[50vh] bg-screen pb-mobile-screen-b pl-mobile-screen-l pr-mobile-screen-r pt-mobile-screen-t lg:pb-screen-b lg:pl-screen-l lg:pr-screen-r lg:pt-screen-t'
      )}
    >
      {React.Children.toArray(
        uiComponents.map((uiComponent) => {
          const feedData = feedsByComponentId.get(uiComponent.id);

          const error: string | undefined = feedData?.error;
          const feed = feedData?.feed as Feed | null | undefined;

          return (
            <div className="w-full" key={uiComponent.id}>
              {debug === 'on' ? (
                <DebugComponent
                  uiComponent={uiComponent as UIComponent}
                  feed={feed}
                  error={error}
                />
              ) : null}

              <UiComponent
                uiComponent={uiComponent as UIComponent}
                feed={feed}
                {...uiComponentProps}
              />
            </div>
          );
        })
      )}
    </div>
  );
}
